/**
 * Detects the carrier from a tracking number and returns the appropriate tracking URL.
 * Supports FedEx, USPS, and UPS.
 */
export type TrackingCarrier = 'fedex' | 'usps' | 'ups'

/**
 * Detect carrier from tracking number using known format patterns.
 * - UPS: 1Z followed by 16 alphanumeric (18 chars total)
 * - USPS: 20-22 digits starting with 9, or ends with "US"
 * - FedEx: 12, 15, 20, or 22 digits (default when no other pattern matches)
 */
export function detectTrackingCarrier(trackingNumber: string): TrackingCarrier {
  const trimmed = trackingNumber.replace(/\s/g, '').trim()

  // UPS: 1Z + 16 alphanumeric
  if (/^1Z[0-9A-Z]{16}$/i.test(trimmed)) {
    return 'ups'
  }

  // USPS: ends with "US" (international format, e.g. EC 000 000 000 US)
  if (/^[A-Z0-9\s]{10,30}US$/i.test(trimmed)) {
    return 'usps'
  }

  // USPS: 20-22 digits; domestic tracking numbers all start with 9 (94, 92, 93, 95 Certified, etc.)
  if (/^\d{20,22}$/.test(trimmed) && trimmed.startsWith('9')) {
    return 'usps'
  }

  // FedEx: 12, 15, 20, or 22 digits
  if (/^\d{12}$|^\d{15}$|^\d{20}$|^\d{22}$/.test(trimmed)) {
    return 'fedex'
  }

  // Default to FedEx for unknown numeric-like formats (e.g. other digit lengths)
  return 'fedex'
}

const TRACKING_URLS: Record<TrackingCarrier, (tn: string) => string> = {
  fedex: (tn) => `https://www.fedex.com/fedextrack/?trknbr=${encodeURIComponent(tn)}`,
  usps: (tn) =>
    `https://tools.usps.com/go/TrackConfirmAction?tLabels=${encodeURIComponent(tn.replace(/\s/g, ''))}`,
  ups: (tn) => `https://www.ups.com/track?tracknum=${encodeURIComponent(tn)}`,
}

/**
 * Returns the carrier's tracking URL for the given tracking number.
 */
export function getTrackingUrl(trackingNumber: string): string {
  const carrier = detectTrackingCarrier(trackingNumber)
  return TRACKING_URLS[carrier](trackingNumber)
}
